import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
} from 'grommet';
import { useOutletContext, useParams } from 'react-router-dom';
import { API } from '@enrich/shared-lib';
import QueryString from 'qs';

export const NotMatchedStudents = () => {
    const user = useOutletContext();
    const { schoolSlug } = useParams();
    const school = user.school;
    const stepOptions = [25, 50, 100];
    const [students, setStudents] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(stepOptions[0]);

    useEffect(() => {
        if (!page) return;
        let isActive = true;

        (async () => {
            const response = await loadStudents({
                schoolId: school.id,
                page,
                pageSize,
            });

            if (isActive) {
                setPage(response.meta.pagination.page);
                setStudents(response.data);
                setTotalItems(response.meta.pagination.total);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [school.id, page, pageSize]);

    const handlePageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (pageSize !== newPageSize) {
            setPageSize(newPageSize);
            setPage(1);
            return;
        }
        setPage(page);
    };

    const dataColumns = [
        {
            header: 'Name',
            property: 'attributes.lastName',
            render: (contact) => (
                <Anchor
                    key={contact.id}
                    href={`/${schoolSlug}/students/${contact.id}`}
                    label={`${contact.attributes.firstName} ${contact.attributes.lastName}`}
                />
            ),
            sortable: true,
        },
        {
            header: 'Graduation',
            property: 'attributes.graduationYear',
            render: (contact) => {
                const semester = contact.attributes.graduationSemester;
                const formattedSemester = semester
                    ? semester[0].toUpperCase() + semester.slice(1)
                    : '';
                return `${formattedSemester} ${contact.attributes.graduationYear}`;
            },
        },
        {
            header: 'Majors',
            property: 'attributes.majors',
            render: (contact) => {
                const majors = contact?.attributes.majors?.data?.map(
                    (major) => major.attributes.name,
                );
                return majors?.length > 0 ? majors.join(', ') : 'N/A';
            },
        },
        {
            header: 'Status',
            property: 'attributes.enrichProgramStatus',
            render: (contact) => contact.attributes.enrichProgramStatus,
            sortable: true,
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box fill="horizontal">
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="3">Not Matched</Heading>
                    </Box>
                    <Box>
                        <DataTable
                            primaryKey="id"
                            sortable
                            border="bottom"
                            data={students}
                            columns={dataColumns}
                        />
                    </Box>
                    <Box align="center" justify="center" pad="small">
                        <Pagination
                            numberItems={totalItems}
                            onChange={({ page, startIndex, endIndex }) => {
                                handlePageChange({ page, startIndex, endIndex });
                            }}
                            step={pageSize}
                            stepOptions={stepOptions}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const loadStudents = async ({ schoolId, page, pageSize } = {}) => {
    const query = QueryString.stringify({
        filters: {
            enrichProgramStatus: 'active',
            school: schoolId,
            candidacies: { id: { $null: true } },
        },
        populate: ['majors'],
        pagination: page && pageSize ? { page, pageSize } : undefined,
        sort: ['updatedAt:desc'],
    });

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
        return { data: [], meta: { pagination: { page: 1, total: 0 } } };
    }
};
